import React, { useState, useRef, useEffect, useCallback } from "react";

interface Message {
  role: "user" | "assistant";
  content: string;
  id: string;
}

type View = "chat" | "ticket" | "ticket-sent";


const SUGGESTED = [
  "How do I set my own price for a tube?",
  "What does the Gold tier mean?",
  "How do I create a sales offer?",
  "What's the Founders Program?",
  "How do I add tubes to inventory?",
  "Does TubeDealer work with modern JJ, Sovtek, EHX, or Psvane tubes?",
  "What are common 12AX7 equivalents?",
];

const TICKET_CATEGORIES = [
  "Billing & Subscription",
  "Scanning & Camera",
  "Inventory & Offers",
  "TubeDealer Marketplace",
  "Account & Login",
  "Bug / Error Report",
  "Feature Request",
  "Other",
];

const GREETING = (username: string) =>
  `Hi ${username}! 👋 I'm your TubeDealer Assistant — I know TubeDealer inside and out. Ask me anything about scanning tubes, pricing, inventory, offers, the marketplace, or vacuum tubes in general. I'm here whenever you need me!`;

export function TubeDealerAssistant({ username }: { username: string }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>("chat");
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [pulse, setPulse] = useState(true);
  const [bubbleHover, setBubbleHover] = useState(false);

  // Ticket form state
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketCategory, setTicketCategory] = useState(TICKET_CATEGORIES[0]);
  const [ticketMessage, setTicketMessage] = useState("");
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [ticketError, setTicketError] = useState<string | null>(null);

  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const greeted = useRef(false);

  // ── First-login greeting ──────────────────────────────────────────────────
  useEffect(() => {
    if (!username || greeted.current) return;
    greeted.current = true;
    const key = `td_greeted_${username}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    // Auto-open after a brief delay and inject a greeting
    const t = setTimeout(() => {
      setOpen(true);
      setMessages([{
        role: "assistant",
        content: GREETING(username),
        id: "greeting",
      }]);
    }, 1800);
    return () => clearTimeout(t);
  }, [username]);

  // ── Stop bubble pulse on first open ──────────────────────────────────────
  useEffect(() => {
    if (open) setPulse(false);
  }, [open]);

  // ── Auto-scroll ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (open && view === "chat") bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open, streaming, view]);

  // ── Focus input ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (open && view === "chat") setTimeout(() => inputRef.current?.focus(), 100);
  }, [open, view]);

  // ── Send message ──────────────────────────────────────────────────────────
  const sendMessage = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || streaming) return;

    const userMsg: Message = { role: "user", content: trimmed, id: Date.now().toString() };
    const assistantId = (Date.now() + 1).toString();

    setMessages(prev => [
      ...prev,
      userMsg,
      { role: "assistant", content: "", id: assistantId },
    ]);
    setInput("");
    setStreaming(true);

    const history = [...messages, userMsg].map(m => ({ role: m.role, content: m.content }));

    const abort = new AbortController();
    abortRef.current = abort;

    try {
      const res = await fetch(`/api/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ messages: history }),
        signal: abort.signal,
      });

      if (!res.ok || !res.body) throw new Error("Network error");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(line.slice(6)) as { content?: string; done?: boolean; error?: string };
            if (payload.done) break;
            if (payload.error) {
              setMessages(prev => prev.map(m =>
                m.id === assistantId ? { ...m, content: payload.error! } : m
              ));
              break;
            }
            if (payload.content) {
              setMessages(prev => prev.map(m =>
                m.id === assistantId ? { ...m, content: m.content + payload.content! } : m
              ));
            }
          } catch { /* ignore malformed lines */ }
        }
      }
    } catch (err: unknown) {
      if ((err as { name?: string })?.name !== "AbortError") {
        setMessages(prev => prev.map(m =>
          m.id === assistantId
            ? { ...m, content: "Sorry, I had trouble connecting. Please try again." }
            : m
        ));
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [messages, streaming]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void sendMessage(input);
    }
  };

  const clearChat = () => {
    abortRef.current?.abort();
    setMessages([]);
    setStreaming(false);
  };

  // ── Submit support ticket ─────────────────────────────────────────────────
  const submitTicket = async () => {
    if (!ticketSubject.trim() || !ticketMessage.trim()) {
      setTicketError("Please fill in the subject and description.");
      return;
    }
    setTicketSubmitting(true);
    setTicketError(null);
    try {
      const r = await fetch(`/api/support/tickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          category: ticketCategory,
          subject: ticketSubject.trim(),
          message: ticketMessage.trim(),
          priority: "Medium",
        }),
      });
      if (!r.ok) {
        const d = await r.json() as { error?: string };
        setTicketError(d.error ?? "Failed to submit ticket.");
        return;
      }
      setView("ticket-sent");
      setTicketSubject("");
      setTicketCategory(TICKET_CATEGORIES[0]);
      setTicketMessage("");
      setTimeout(() => {
        setView("chat");
        setMessages(prev => [
          ...prev,
          {
            role: "assistant",
            content: "Your support ticket has been submitted! ✅ Our team typically responds within 24–48 hours. Is there anything else I can help you with in the meantime?",
            id: Date.now().toString(),
          },
        ]);
      }, 2800);
    } catch {
      setTicketError("Network error. Please try again.");
    } finally {
      setTicketSubmitting(false);
    }
  };

  const hasExchanges = messages.length >= 2;

  // ── Shared header ─────────────────────────────────────────────────────────
  const Header = (
    <div style={{
      background: "linear-gradient(135deg, #0a1f15 0%, #0f2d1d 100%)",
      borderBottom: "1px solid #00ff8820",
      padding: "14px 16px",
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexShrink: 0,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: "50%",
        background: "radial-gradient(circle at 35% 35%, #00ff88 0%, #00994d 50%, #003322 100%)",
        boxShadow: "0 0 12px rgba(0,255,136,0.5), inset 0 1px 3px rgba(255,255,255,0.2)",
        animation: "orbGlow 3s ease-in-out infinite",
        flexShrink: 0,
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#00ff88", letterSpacing: "0.02em" }}>
          {view === "chat" ? "TubeDealer Assistant" : view === "ticket-sent" ? "Ticket Submitted!" : "Open a Support Ticket"}
        </div>
        <div style={{ fontSize: 10, color: "#3a6a4a", display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#00ff88", display: "inline-block", animation: "statusPulse 2s ease-in-out infinite" }} />
          TubeDealer AI · Online
        </div>
      </div>
      {view === "chat" && messages.length > 0 && (
        <button onClick={clearChat} title="Clear conversation"
          style={{ background: "transparent", border: "none", color: "#2a4a3a", cursor: "pointer", fontSize: 11, padding: "4px 8px", borderRadius: 6, fontFamily: "inherit" }}>
          Clear
        </button>
      )}
      {view === "ticket" && (
        <button onClick={() => { setView("chat"); setTicketError(null); }} title="Back to chat"
          style={{ background: "transparent", border: "none", color: "#2a4a3a", cursor: "pointer", fontSize: 11, padding: "4px 8px", borderRadius: 6, fontFamily: "inherit" }}>
          ← Chat
        </button>
      )}
    </div>
  );

  return (
    <>
      {/* ── Floating bubble ── */}
      {/* Tooltip above bubble (desktop hover, panel closed) */}
      {!open && bubbleHover && (
        <div style={{
          position: "fixed", bottom: 78, right: 20,
          background: "#141414", border: "1px solid #2e2416",
          borderRadius: 8, padding: "7px 12px",
          color: "#d4b87a", fontSize: 11, fontWeight: 500,
          whiteSpace: "nowrap", zIndex: 10001, pointerEvents: "none",
          boxShadow: "0 6px 18px rgba(0,0,0,0.55)",
          animation: "tdTooltipIn 0.12s ease",
        }}>
          Chat with the TubeDealer AI assistant
        </div>
      )}

      <button
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setBubbleHover(true)}
        onMouseLeave={() => setBubbleHover(false)}
        aria-label="TubeDealer Assistant"
        style={{
          position: "fixed", bottom: 24, right: 24,
          width: open ? 44 : "auto", height: 44, borderRadius: 22,
          padding: open ? "0" : "0 16px",
          background: "linear-gradient(135deg, #0d2b1f 0%, #1a5c35 60%, #00ff88 100%)",
          border: "2px solid #00ff8855",
          boxShadow: open
            ? "0 0 0 3px #00ff8830, 0 8px 32px rgba(0,255,136,0.35)"
            : pulse
              ? "0 0 0 4px #00ff8840, 0 6px 24px rgba(0,255,136,0.3)"
              : "0 4px 20px rgba(0,0,0,0.6)",
          cursor: "pointer", zIndex: 10000,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
          transition: "all 0.25s ease", whiteSpace: "nowrap",
          animation: pulse && !open ? "assistantPulse 2.5s ease-in-out infinite" : "none",
        }}
      >
        {open ? (
          <span style={{ fontSize: 16, color: "#00ff88", fontWeight: 700 }}>✕</span>
        ) : (
          <>
            <span style={{ fontSize: 11, color: "#00ff88", fontWeight: 900, letterSpacing: "0.04em", lineHeight: 1 }}>TD</span>
            <span style={{ width: 1, height: 14, background: "#00ff8840", flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: "#fff", fontWeight: 700, letterSpacing: "0.06em", lineHeight: 1 }}>AI HELP</span>
          </>
        )}
      </button>

      {/* ── Chat panel ── */}
      {open && (
        <div style={{
          position: "fixed", bottom: 96, right: 16,
          width: "min(420px, calc(100vw - 32px))",
          height: "min(620px, calc(100vh - 120px))",
          background: "#080f0b",
          border: "1px solid #00ff8830",
          borderRadius: 18,
          boxShadow: "0 0 0 1px #00ff8815, 0 24px 80px rgba(0,0,0,0.85), 0 0 60px rgba(0,255,136,0.08)",
          display: "flex", flexDirection: "column",
          zIndex: 9999, overflow: "hidden",
          animation: "assistantSlideUp 0.22s cubic-bezier(0.34,1.56,0.64,1)",
        }}>

          {Header}

          {/* ── CHAT VIEW ── */}
          {view === "chat" && (
            <>
              {/* Messages */}
              <div style={{
                flex: 1, overflowY: "auto", padding: "16px 14px",
                display: "flex", flexDirection: "column", gap: 12,
                scrollbarWidth: "thin", scrollbarColor: "#1a3a2a transparent",
              }}>
                {messages.length === 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    <div style={{
                      background: "linear-gradient(135deg, #0a1f15, #0d2b1d)",
                      border: "1px solid #00ff8820", borderRadius: 14, padding: "14px 16px", textAlign: "center",
                    }}>
                      <div style={{ fontSize: 28, marginBottom: 8 }}>🎛️</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#00ff88", marginBottom: 4 }}>
                        Hey {username ? username : "there"}!
                      </div>
                      <div style={{ fontSize: 12, color: "#3a6a4a", lineHeight: 1.6 }}>
                        I'm your TubeDealer Assistant. Ask me anything about the software, your tubes, pricing, or the marketplace.
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: "#2a4a3a", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8, paddingLeft: 2 }}>
                        Common questions
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {SUGGESTED.map((q) => (
                          <button key={q} onClick={() => void sendMessage(q)}
                            style={{
                              background: "#0a1a10", border: "1px solid #1a3a2a",
                              borderRadius: 10, padding: "9px 13px", color: "#4a9a6a",
                              fontSize: 12, cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                              transition: "all 0.15s ease",
                            }}
                            onMouseEnter={e => {
                              (e.currentTarget as HTMLElement).style.borderColor = "#00ff8840";
                              (e.currentTarget as HTMLElement).style.color = "#00dd77";
                            }}
                            onMouseLeave={e => {
                              (e.currentTarget as HTMLElement).style.borderColor = "#1a3a2a";
                              (e.currentTarget as HTMLElement).style.color = "#4a9a6a";
                            }}
                          >{q}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div key={msg.id} style={{
                      display: "flex",
                      flexDirection: msg.role === "user" ? "row-reverse" : "row",
                      alignItems: "flex-end", gap: 8,
                    }}>
                      {msg.role === "assistant" && (
                        <div style={{
                          width: 26, height: 26, borderRadius: "50%",
                          background: "radial-gradient(circle at 35% 35%, #00ff88, #00994d 60%, #002a18)",
                          boxShadow: "0 0 8px rgba(0,255,136,0.35)", flexShrink: 0,
                        }} />
                      )}
                      <div style={{
                        maxWidth: "78%",
                        background: msg.role === "user"
                          ? "linear-gradient(135deg, #0a3a20, #0d4a28)"
                          : "#0c1a12",
                        border: msg.role === "user" ? "1px solid #00ff8835" : "1px solid #1a2e20",
                        borderRadius: msg.role === "user" ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
                        padding: "10px 13px", fontSize: 13,
                        color: msg.role === "user" ? "#c8f0dc" : "#a8c8b0",
                        lineHeight: 1.55, whiteSpace: "pre-wrap", wordBreak: "break-word",
                        boxShadow: msg.role === "user" ? "0 2px 12px rgba(0,255,136,0.1)" : "none",
                      }}>
                        {msg.content || (
                          <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            {[0, 1, 2].map(i => (
                              <span key={i} style={{
                                width: 6, height: 6, borderRadius: "50%", background: "#00ff88",
                                opacity: 0.5, animation: `assistantDot 1.2s ease-in-out ${i * 0.2}s infinite`,
                                display: "inline-block",
                              }} />
                            ))}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
                <div ref={bottomRef} />
              </div>

              {/* Still need help? link — shown after at least one AI exchange */}
              {hasExchanges && !streaming && (
                <div style={{ padding: "0 14px 8px", display: "flex", justifyContent: "center" }}>
                  <button
                    onClick={() => setView("ticket")}
                    style={{
                      background: "transparent", border: "1px solid #1a3a24",
                      borderRadius: 8, padding: "7px 14px", color: "#3a6a4a",
                      fontSize: 11, cursor: "pointer", fontFamily: "inherit",
                      display: "flex", alignItems: "center", gap: 6,
                      transition: "all 0.15s ease",
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.borderColor = "#00ff8830";
                      (e.currentTarget as HTMLElement).style.color = "#5a9a6a";
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.borderColor = "#1a3a24";
                      (e.currentTarget as HTMLElement).style.color = "#3a6a4a";
                    }}
                  >
                    <span style={{ fontSize: 13 }}>📮</span>
                    Still need help? Open a support ticket
                  </button>
                </div>
              )}

              {/* Input bar */}
              <div style={{
                borderTop: "1px solid #0f2a1a", padding: "12px 14px",
                background: "#070e09", flexShrink: 0,
              }}>
                <div style={{
                  display: "flex", gap: 8, alignItems: "flex-end",
                  background: "#0c1a10", border: "1px solid #1a3a22",
                  borderRadius: 14, padding: "8px 8px 8px 13px", transition: "border-color 0.2s",
                }}
                  onFocusCapture={e => (e.currentTarget as HTMLElement).style.borderColor = "#00ff8845"}
                  onBlurCapture={e => (e.currentTarget as HTMLElement).style.borderColor = "#1a3a22"}
                >
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask anything about tubes or TubeDealer…"
                    aria-label="Ask anything about tubes or TubeDealer"
                    rows={1}
                    disabled={streaming}
                    style={{
                      flex: 1, background: "transparent", border: "none", outline: "none",
                      color: "#c8f0dc", fontSize: 13, fontFamily: "inherit",
                      resize: "none", lineHeight: 1.5, maxHeight: 100,
                      overflowY: "auto", padding: 0,
                    }}
                    onInput={e => {
                      const el = e.currentTarget;
                      el.style.height = "auto";
                      el.style.height = Math.min(el.scrollHeight, 100) + "px";
                    }}
                  />
                  <button
                    onClick={() => void sendMessage(input)}
                    disabled={!input.trim() || streaming}
                    style={{
                      width: 34, height: 34, borderRadius: 10,
                      background: input.trim() && !streaming
                        ? "linear-gradient(135deg, #00aa55, #00ff88)"
                        : "#0f1f14",
                      border: "none",
                      cursor: input.trim() && !streaming ? "pointer" : "default",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, transition: "all 0.2s ease",
                      boxShadow: input.trim() && !streaming ? "0 2px 12px rgba(0,255,136,0.3)" : "none",
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M2 8L14 8M14 8L9 3M14 8L9 13" stroke={input.trim() && !streaming ? "#000" : "#2a4a3a"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
                <div style={{ fontSize: 9, color: "#1a3a22", textAlign: "center", marginTop: 6, letterSpacing: "0.04em" }}>
                  TubeDealer AI · Enter to send · Shift+Enter for new line
                </div>
              </div>
            </>
          )}

          {/* ── TICKET FORM VIEW ── */}
          {view === "ticket" && (
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Try AI first nudge */}
              <div style={{
                background: "#0a1f15", border: "1px solid #00ff8820",
                borderRadius: 12, padding: "12px 14px",
                display: "flex", alignItems: "flex-start", gap: 10,
              }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>💬</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#00ff88", marginBottom: 4 }}>
                    Have you tried the AI assistant?
                  </div>
                  <div style={{ fontSize: 11, color: "#4a7a5a", lineHeight: 1.55, marginBottom: 8 }}>
                    Our AI often answers questions instantly — it knows TubeDealer inside and out.
                  </div>
                  <button
                    onClick={() => setView("chat")}
                    style={{
                      background: "#0f2a1a", border: "1px solid #00ff8830",
                      borderRadius: 7, padding: "6px 12px", color: "#00cc66",
                      fontSize: 11, cursor: "pointer", fontFamily: "inherit", fontWeight: 600,
                    }}
                  >
                    ← Ask the AI Assistant
                  </button>
                </div>
              </div>

              <div style={{ fontSize: 12, color: "#4a7a5a", lineHeight: 1.55 }}>
                Our support team typically responds within <strong style={{ color: "#7aaa8a" }}>24–48 hours</strong> (Mon–Fri).
              </div>

              {/* Category */}
              <div>
                <label htmlFor="tda-category" style={{ fontSize: 11, color: "#3a6a4a", display: "block", marginBottom: 5, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                  Category
                </label>
                <select
                  id="tda-category"
                  value={ticketCategory}
                  onChange={e => setTicketCategory(e.target.value)}
                  style={{
                    width: "100%", background: "#0c1a10", border: "1px solid #1a3a22",
                    borderRadius: 9, padding: "9px 12px", color: "#c8f0dc",
                    fontSize: 13, fontFamily: "inherit", outline: "none",
                  }}
                >
                  {TICKET_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Subject */}
              <div>
                <label htmlFor="tda-subject" style={{ fontSize: 11, color: "#3a6a4a", display: "block", marginBottom: 5, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                  Subject
                </label>
                <input
                  id="tda-subject"
                  type="text"
                  value={ticketSubject}
                  onChange={e => setTicketSubject(e.target.value)}
                  placeholder="Brief description of your issue"
                  maxLength={120}
                  style={{
                    width: "100%", background: "#0c1a10", border: "1px solid #1a3a22",
                    borderRadius: 9, padding: "9px 12px", color: "#c8f0dc",
                    fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box",
                  }}
                />
              </div>

              {/* Message */}
              <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <label htmlFor="tda-description" style={{ fontSize: 11, color: "#3a6a4a", display: "block", marginBottom: 5, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                  Description
                </label>
                <textarea
                  id="tda-description"
                  value={ticketMessage}
                  onChange={e => setTicketMessage(e.target.value)}
                  placeholder="Describe your issue in detail…"
                  rows={5}
                  style={{
                    flex: 1, background: "#0c1a10", border: "1px solid #1a3a22",
                    borderRadius: 9, padding: "9px 12px", color: "#c8f0dc",
                    fontSize: 13, fontFamily: "inherit", outline: "none",
                    resize: "vertical", minHeight: 90,
                  }}
                />
              </div>

              {ticketError && (
                <div style={{ fontSize: 12, color: "#ff6b6b", background: "#1a0808", border: "1px solid #4a1a1a", borderRadius: 8, padding: "8px 12px" }}>
                  {ticketError}
                </div>
              )}

              <button
                onClick={() => void submitTicket()}
                disabled={ticketSubmitting || !ticketSubject.trim() || !ticketMessage.trim()}
                style={{
                  background: ticketSubmitting || !ticketSubject.trim() || !ticketMessage.trim()
                    ? "#0f1f14"
                    : "linear-gradient(135deg, #00aa55, #00ff88)",
                  border: "none", borderRadius: 11, padding: "12px",
                  color: ticketSubmitting || !ticketSubject.trim() || !ticketMessage.trim()
                    ? "#2a4a3a"
                    : "#001a0a",
                  fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  transition: "all 0.2s ease",
                  boxShadow: ticketSubmitting || !ticketSubject.trim() || !ticketMessage.trim()
                    ? "none"
                    : "0 3px 16px rgba(0,255,136,0.25)",
                }}
              >
                {ticketSubmitting ? "Submitting…" : "Submit Support Ticket"}
              </button>
            </div>
          )}

          {/* ── TICKET SENT VIEW ── */}
          {view === "ticket-sent" && (
            <div style={{
              flex: 1, display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              padding: "32px 24px", gap: 16, textAlign: "center",
            }}>
              <div style={{ fontSize: 48 }}>✅</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#00ff88" }}>Ticket Submitted!</div>
              <div style={{ fontSize: 12, color: "#4a7a5a", lineHeight: 1.6 }}>
                Our support team will get back to you within{" "}
                <strong style={{ color: "#7aaa8a" }}>24–48 hours</strong>.<br />
                Returning to the AI assistant…
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes assistantPulse {
          0%, 100% { box-shadow: 0 0 0 4px #00ff8820, 0 6px 24px rgba(0,255,136,0.2); }
          50% { box-shadow: 0 0 0 8px #00ff8810, 0 8px 30px rgba(0,255,136,0.4); }
        }
        @keyframes assistantSlideUp {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes orbGlow {
          0%, 100% { box-shadow: 0 0 12px rgba(0,255,136,0.4), inset 0 1px 3px rgba(255,255,255,0.15); }
          50% { box-shadow: 0 0 22px rgba(0,255,136,0.7), inset 0 1px 3px rgba(255,255,255,0.25); }
        }
        @keyframes statusPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes assistantDot {
          0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
          40% { transform: scale(1.1); opacity: 1; }
        }
      `}</style>
    </>
  );
}
